<script setup lang="ts">import { ref as _ref } from 'vue';

import type { Place } from '@consumer/types/serializers'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import { setTaglineUnlessCustom } from '@consumer/logic/giftly'
import {
  type PlaceSearchResults,
  useDefaultLocation,
  useGeneralPlaces,
  canViewMore,
  searchPlaces,
} from '@consumer/services/api/customizer'
import { last, xor } from '@consumer/helpers/array'
import { presence } from '@shared/object'

import GeneralPlaces from '@consumer/modules/customizer/steps/stepPlaces/GeneralPlaces.vue'
import LocalMerchant from '@consumer/modules/customizer/steps/stepPlaces/LocalMerchant.vue'
import MerchantSearchForm from '@consumer/modules/customizer/steps/stepPlaces/MerchantSearchForm.vue'

withDefaults(defineProps<{ gift: GiftTemplate; createOrEditMultiplace: boolean }>(), {  })

const emit = defineEmits<{ stepComplete: [] }>()

let placeSearchTerm = _ref<string>()
let placeSearchLocation = (useDefaultLocation())
watchEffect(() => {
  placeSearchTerm.value = last(__props.gift.giftlyMetadata?.placeSearchTerms)
  placeSearchLocation.value = last(__props.gift.giftlyMetadata?.placeSearchLocations)
})

const searchResults = reactive<PlaceSearchResults | Partial<PlaceSearchResults>>({})

const hasSearchResults = computed(() => searchResults.items?.length)

let isLoading = _ref(false)
let isSearching = _ref(false)
let isLoadingMore = _ref(false)

const pageSize = 8

watch(() => isLoading.value, () => {
  if (!isLoading.value) {
    isSearching.value = isLoadingMore.value = false
  }
})

function searchPlacesBy (searchTerms: string, searchLocation: string) {
  isLoading.value = isSearching.value = true
  searchPlaces({
    searchTerms,
    searchLocation,
    page: 1,
    pageSize,
    isMultiplaceBuilder: __props.createOrEditMultiplace,
  }).then((data) => {
    placeSearchTerm.value = searchTerms
    placeSearchLocation.value = searchLocation
    Object.assign(searchResults, data)
    isLoading.value = false

    __props.gift.giftlyMetadata ||= {}
    __props.gift.giftlyMetadata.placeSearchTerms ||= []
    __props.gift.giftlyMetadata.placeSearchLocations ||= []

    if (last(__props.gift.giftlyMetadata?.placeSearchTerms) !== placeSearchTerm.value) {
      __props.gift.giftlyMetadata.placeSearchTerms = __props.gift.giftlyMetadata.placeSearchTerms?.concat(
        placeSearchTerm.value,
      )
    }

    if (last(__props.gift.giftlyMetadata?.placeSearchLocations) !== placeSearchLocation.value) {
      __props.gift.giftlyMetadata.placeSearchLocations = __props.gift.giftlyMetadata.placeSearchLocations?.concat(
        placeSearchLocation.value,
      )
    }
  })
}

function loadMorePlaces () {
  isLoading.value = isLoadingMore.value = true
  searchPlaces({
    searchTerms: placeSearchTerm.value!,
    searchLocation: placeSearchLocation.value!,
    page: (searchResults.page ?? 1) + 1,
    pageSize,
  }).then((data) => {
    data.items = [...(searchResults.items || []), ...data.items]
    Object.assign(searchResults, data)
    isLoading.value = false
  })
}

function resetSearchResults () {
  searchResults.items = undefined
  searchResults.page = undefined
  searchResults.total = undefined
  placeSearchTerm.value = undefined
}

let errorMessage = _ref<string>()

const topGeneralPlaces = (useGeneralPlaces())

const MAX_PLACE_COUNT = 100

function togglePlace (place: Place) {
  const placeId = [String(place.giftlyId)]

  const placeIds = xor(__props.gift.places?.map(place => String(place.giftlyId)) ?? [], placeId)

  if (placeIds.length > MAX_PLACE_COUNT) {
    errorMessage.value = `You may suggest only up to ${MAX_PLACE_COUNT} places.`
    return
  }

  const currentPlaces = __props.gift.places

  __props.gift.places = placeIds.map((id) => {
    const currentPlace = currentPlaces?.find(currentPlace => String(currentPlace.giftlyId) === String(id))
    if (currentPlace) {
      return currentPlace
    }
    if (String(place.giftlyId) === String(id)) {
      return place
    }
    return topGeneralPlaces.value?.find(generalPlace => String(generalPlace.giftlyId) === String(id))
  }).filter(Boolean)

  if (!__props.gift.giftlyMetadata?.isCustomTagline) {
    setTaglineUnlessCustom(__props.gift, false)
  }
  if (placeIds.length) { emit('stepComplete') }
}

const initialPlaces = [{ name: 'Any place' }]

function isPlaceSelected (place: Place) {
  const selectedPlaces = presence(__props.gift.places) || [{ name: 'Any place' }]

  return selectedPlaces.some(selectedPlace =>
    (selectedPlace.id && selectedPlace.id === place.id)
    || (selectedPlace.giftlyId && selectedPlace.giftlyId === place.giftlyId)
    || (
      (!place.placeType || place.placeType === 'general')
      && selectedPlace.name
      && selectedPlace.name.toLowerCase() === place.name?.toLowerCase()
    ),
  )
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <MerchantSearchForm
      :gift="gift"
      @submit="searchPlacesBy"
      @reset="resetSearchResults"
    />

    <GMessage v-if="errorMessage" :text="errorMessage" type="error"/>

    <template v-if="hasSearchResults">
      <div class="flex items-center text-sm">
        <span class="text-grey-700 text-sm ml-1">
          {{ searchResults.total }} Results
        </span>
        <div v-if="isSearching" class="ml-4 flex items-center justify-center gap-2">
          <LoadingIndicator size="sm" class="text-v2-secondary"/>
          Searching...
        </div>
      </div>
      <div class="w-full grid gr-cols-1 lg:grid-cols-2 gap-2">
        <LocalMerchant
          v-for="place in searchResults.items"
          :key="place.id"
          :place="place"
          :selected="isPlaceSelected(place)"
          @click="togglePlace(place)"
        />
        <GButton
          v-if="!isLoadingMore && canViewMore(searchResults)"
          secondary
          size="small"
          name="nc_place_view_more"
          label="View More"
          class="w-full col-span-full"
          @click="loadMorePlaces"
        />
      </div>

      <div v-if="isLoadingMore" class="flex items-center justify-center gap-2 h-10">
        <LoadingIndicator size="lg" class="text-v2-secondary"/>
        Loading...
      </div>
    </template>
    <div v-else-if="searchResults.total === 0" class="flex items-center justify-center gap-2 h-10">
      We found no place matching "{{ placeSearchTerm }}" in {{ placeSearchLocation }}
    </div>
    <div v-else-if="isLoading" class="flex items-center justify-center gap-2 h-10">
      <LoadingIndicator size="lg" class="text-v2-secondary"/>
      Searching...
    </div>

    <GeneralPlaces
      :initialPlaces="initialPlaces"
      :topGeneralPlaces="topGeneralPlaces"
      :isPlaceSelected="isPlaceSelected"
      search
      @togglePlace="togglePlace"
    />
  </div>
</template>
